import React from "react";
import { Link, Skeleton, Stack, Typography, Avatar } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArrowCircleDownRoundedIcon from '@mui/icons-material/ArrowCircleDownRounded';
import { useTranslation } from "react-i18next";
import { useMessageMediaAccessQuery } from "./useMessageMediaAccessQuery";

const DocumentMessageContent = ({ media, messageUuid }) => {
  const { t } = useTranslation();
  const isReady = media?.status === "ready";
  const { data, isLoading, isError } = useMessageMediaAccessQuery(
    messageUuid,
    Boolean(messageUuid) && isReady,
  );
  const documentUrl = data?.url ?? media?.signedUrl;
  const fileLabel = media?.filename || t("messages.media.document.document");
  const extension = media?.filename?.split(".").pop()?.toUpperCase();
  const mediaBytes = media?.size?.processed ?? 0;
  const mimeType = media?.mime_type;
  const fileSize =
    mediaBytes > 0 ? `${(mediaBytes / 1024).toFixed(2)} kB` : null;
  const detail = [extension, fileSize].filter(Boolean).join(" · ");

  if (media?.status === "failed" || isError) {
    return (
      <Typography variant="body2">
        {t("messages.media.document.error")}
      </Typography>
    );
  }

  // Anything other than 'ready' (null, 'pending', 'processing') means the
  // media service hasn't finished yet — keep the skeleton until it has.
  if (!isReady || isLoading) {
    return (
      <Skeleton variant="rounded" height={64} sx={{ borderRadius: 1.5 }} />
    );
  }

  if (!documentUrl) {
    return (
      <Typography variant="body2">
        {t("messages.media.document.unavailable")}
      </Typography>
    );
  }

  return (
    <Stack
      component={Link}
      href={documentUrl}
      target="_blank"
      rel="noreferrer"
      underline="none"
      color="inherit"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
      sx={{ p: 1, borderRadius: 1.5, bgcolor: "action.hover", minWidth: 220 }}
    >
      <Avatar variant="rounded" sx={{ bgcolor: "error.main" }}>
        <InsertDriveFileIcon fontSize="small" />
      </Avatar>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {fileLabel}
        </Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {detail}
          </Typography>
        )}
      </Stack>
      <ArrowCircleDownRoundedIcon color="action" />
    </Stack>
  );
};

export default DocumentMessageContent;
